<template>
  <div class="bg-card rounded-lg shadow p-6 min-h-[calc(100vh-120px)]">
    <!-- Process Selector Header -->
    <div class="mb-6 flex justify-between items-center">
      <h2 class="text-xl font-semibold">Presentation Management</h2>
      <div class="flex items-center space-x-3">
        <div class="flex items-center">
          <label for="process-selector" class="mr-2 text-sm font-medium text-gray-700">Forecasting Process:</label>
          <select
              id="process-selector"
              v-model="selectedProcessId"
              @change="handleProcessChange"
              class="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-64 p-2.5"
              :disabled="loading"
          >
            <option v-if="loading" value="">Loading processes...</option>
            <option v-else-if="processes.length === 0" value="">No processes available</option>
            <option v-for="process in processes" :key="process.id" :value="process.id">
              {{ process.name }} ({{ formatProcessDate(process.start_date) }})
            </option>
          </select>
          <div v-if="loading" class="ml-2">
            <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
        
        <!-- Action buttons in header -->
        <div class="flex space-x-3">
          <button
              @click="savePresentation"
              class="px-4 py-2 border border-primary text-primary bg-white rounded-md hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center"
              :disabled="isSavingPresentation"
          >
            <span v-if="isSavingPresentation" class="mr-2">
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save Presentation
          </button>
          
          <button
              @click="exportAllSlides"
              class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center"
              :disabled="slides.length === 0 || isExporting"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
            {{ isExporting ? 'Exporting...' : 'Export to PDF' }}
          </button>
          
          <button
              @click="openEmailModal"
              class="px-4 py-2 border border-primary text-primary bg-white rounded-md hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email
          </button>
        </div>
      </div>
    </div>

    <div class="flex gap-4 min-h-[calc(100vh-180px)]">
      <!-- Left side: Slide viewer -->
      <div class="w-1/5 bg-background rounded-md p-4 border border-border flex flex-col">
        <h3 class="text-lg font-semibold mb-2">Presentation Slides</h3>

        <!-- Template selector and Add new slide button -->
        <div class="space-y-2 mb-4">
          <div class="space-y-2">
            <div class="flex items-center space-x-2">
              <select
                  id="template-select"
                  v-model="selectedTemplateId"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              >
                <option v-for="template in htmlTemplates" :key="template.id" :value="template.id">
                  {{ template.name }}
                </option>
              </select>
            </div>
          </div>
          <button
              @click="addNewSlide"
              class="w-full px-4 py-2 bg-white text-primary border border-primary rounded-md hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              :disabled="isLoading"
          >
            {{ isLoading ? 'Loading...' : '+ Add Slide Template' }}
          </button>
        </div>

        <!-- Slides list -->
        <div class="flex-1 overflow-y-auto space-y-3 pr-2">
          <div
              v-for="(slide, index) in slides"
              :key="'slide-' + index"
              class="bg-white p-3 rounded shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <!-- Slide content and reorder buttons -->
            <div class="flex justify-between items-start">
              <div
                  class="font-medium text-sm flex-1 cursor-pointer"
                  @click="selectSlide(index)"
              >
                Slide {{ index + 1 }}
              </div>
              <div class="flex space-x-1">
                <button
                    @click="moveSlide(index, 'up')"
                    class="text-gray-500 hover:text-primary p-1 focus:outline-none"
                    :disabled="index === 0"
                    :class="{ 'opacity-30 cursor-not-allowed': index === 0 }"
                    title="Move Up"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                  </svg>
                </button>
                <button
                    @click="moveSlide(index, 'down')"
                    class="text-gray-500 hover:text-primary p-1 focus:outline-none"
                    :disabled="index === slides.length - 1"
                    :class="{ 'opacity-30 cursor-not-allowed': index === slides.length - 1 }"
                    title="Move Down"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                       stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
                <!-- Delete button -->
                <button
                    @click.stop="deleteSlide(index)"
                    class="text-gray-500 hover:text-red-500 p-1 focus:outline-none"
                    title="Delete Slide"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div
                class="text-xs text-gray-500 mt-1 cursor-pointer"
                @click="selectSlide(index)"
                :class="{ 'font-bold text-primary': selectedSlideIndex === index }"
            >
              {{ slide.title || 'Untitled Slide' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Right side: Slide editor -->
      <div class="w-4/5 bg-white rounded-md shadow p-6 flex flex-col">
        <div v-if="selectedSlide" class="flex-1 flex flex-col">
          <div class="flex justify-between items-center mb-4">
            <!-- Editable title -->
            <div class="flex space-x-2">
                <input
                    id="preview-title"
                    v-model="selectedSlide.title"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-xl font-bold"
                    placeholder="Enter slide title"
                    @change="saveSlide"
                />
              </div>

            <div class="flex space-x-2">
              <button
                  @click="fillContentWithAI"
                  class="px-3 py-1 bg-secondary text-white rounded-md hover:bg-secondary/90 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 text-sm flex items-center"
                  :disabled="isGeneratingContent"
              >
                <span v-if="isGeneratingContent" class="mr-2">
                  <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                       viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
                Fill Content with AI
              </button>
              <button
                  @click="openPromptEditor"
                  class="px-3 py-1 border border-secondary text-secondary rounded-md hover:bg-secondary/10 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 text-sm flex items-center"
                  title="Edit AI Prompt for this template"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Prompt
              </button>
              <!-- Save Presentation button moved to header -->
              <!-- Email button moved to header -->
              <!-- Delete button removed -->
            </div>
          </div>
          
          <!-- Slide editor form -->
          <div class="space-y-4 flex-1 flex flex-col min-h-[500px]">
            <!-- Rich text editor toolbar -->
            <div class="flex items-center space-x-2 p-1 border border-gray-300 rounded-t-md bg-gray-50">
              <button @click="formatText('bold')" class="p-1 hover:bg-gray-200 rounded" title="Bold">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path
                      d="M8.21 13c2.106 0 3.412-1.087 3.412-2.823 0-1.306-.984-2.283-2.324-2.386v-.055a2.176 2.176 0 0 0 1.852-2.14c0-1.51-1.162-2.46-3.014-2.46H3.843V13H8.21zM5.908 4.674h1.696c.963 0 1.517.451 1.517 1.244 0 .834-.629 1.32-1.73 1.32H5.908V4.673zm0 6.788V8.598h1.73c1.217 0 1.88.492 1.88 1.415 0 .943-.643 1.449-1.832 1.449H5.907z"/>
                </svg>
              </button>
              <button @click="formatText('italic')" class="p-1 hover:bg-gray-200 rounded" title="Italic">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path
                      d="M7.991 11.674 9.53 4.455c.123-.595.246-.71 1.347-.807l.11-.52H7.211l-.11.52c1.06.096 1.128.212 1.005.807L6.57 11.674c-.123.595-.246.71-1.346.806l-.11.52h3.774l.11-.52c-1.06-.095-1.129-.211-1.006-.806z"/>
                </svg>
              </button>
              <button @click="formatText('underline')" class="p-1 hover:bg-gray-200 rounded" title="Underline">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path
                      d="M5.313 3.136h-1.23V9.54c0 2.105 1.47 3.623 3.917 3.623s3.917-1.518 3.917-3.623V3.136h-1.23v6.323c0 1.49-.978 2.57-2.687 2.57-1.709 0-2.687-1.08-2.687-2.57V3.136zM12.5 15h-9v-1h9v1z"/>
                </svg>
              </button>
              <div class="h-4 border-l border-gray-300 mx-1"></div>
              <button @click="formatText('insertUnorderedList')" class="p-1 hover:bg-gray-200 rounded"
                      title="Bullet List">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fill-rule="evenodd"
                        d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm-3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                </svg>
              </button>
              <button @click="formatText('insertOrderedList')" class="p-1 hover:bg-gray-200 rounded"
                      title="Numbered List">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fill-rule="evenodd"
                        d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zM1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338v.041zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635V5z"/>
                </svg>
              </button>
              <div class="h-4 border-l border-gray-300 mx-1"></div>
              <button @click="formatText('justifyLeft')" class="p-1 hover:bg-gray-200 rounded" title="Align Left">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fill-rule="evenodd"
                        d="M2 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
                </svg>
              </button>
              <button @click="formatText('justifyCenter')" class="p-1 hover:bg-gray-200 rounded" title="Align Center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fill-rule="evenodd"
                        d="M4 12.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
                </svg>
              </button>
              <div class="h-4 border-l border-gray-300 mx-1"></div>
              <!-- Text color dropdown -->
              <div class="relative inline-block mr-2">
                <button
                    @click="toggleColorPicker('text')"
                    class="p-1 hover:bg-gray-200 rounded flex items-center"
                    title="Text Color"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                       viewBox="0 0 16 16">
                    <path
                        d="M13.354.646a1.207 1.207 0 0 0-1.708 0L8.5 3.793l-.646-.647a.5.5 0 1 0-.708.708l.647.646L4.146 8l-1.5 1.5-.354-.354a.5.5 0 0 0-.708.708l.354.354-.5.5a.5.5 0 0 0 .708.708l.5-.5.354.354a.5.5 0 0 0 .708-.708L3.354 9.5l1.5-1.5L8.5 4.354l.646.646a.5.5 0 0 0 .708-.708l-.647-.646 3.147-3.146a1.207 1.207 0 0 0 0-1.708l-.708.708z"/>
                  </svg>
                  <div class="w-4 h-4 ml-1 border border-gray-300" :style="{ backgroundColor: currentTextColor }"></div>
                </button>
                <!-- Text color picker dropdown -->
                <div
                    v-if="showColorPicker === 'text'"
                    class="absolute z-10 mt-1 bg-white border border-gray-300 rounded shadow-lg p-2"
                    style="width: 200px;"
                >
                  <div class="text-xs font-medium mb-1 text-gray-700">Text Color</div>
                  <div class="grid grid-cols-8 gap-1">
                    <div
                        v-for="color in colorOptions"
                        :key="color"
                        class="w-5 h-5 cursor-pointer border border-gray-300 hover:border-gray-600"
                        :style="{ backgroundColor: color }"
                        @click="applyTextColor(color)"
                    ></div>
                  </div>
                </div>
              </div>

              <!-- Background color dropdown -->
              <div class="relative inline-block">
                <button
                    @click="toggleColorPicker('background')"
                    class="p-1 hover:bg-gray-200 rounded flex items-center"
                    title="Background Color"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                       viewBox="0 0 16 16">
                    <path
                        d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z"/>
                  </svg>
                  <div class="w-4 h-4 ml-1 border border-gray-300" :style="{ backgroundColor: currentBgColor }"></div>
                </button>
                <!-- Background color picker dropdown -->
                <div
                    v-if="showColorPicker === 'background'"
                    class="absolute z-10 mt-1 bg-white border border-gray-300 rounded shadow-lg p-2"
                    style="width: 200px;"
                >
                  <div class="text-xs font-medium mb-1 text-gray-700">Background Color</div>
                  <div class="grid grid-cols-8 gap-1">
                    <div
                        v-for="color in colorOptions"
                        :key="color"
                        class="w-5 h-5 cursor-pointer border border-gray-300 hover:border-gray-600"
                        :style="{ backgroundColor: color }"
                        @click="applyBackgroundColor(color)"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Rich text editor content area with style isolation -->
            <div class="w-full flex-1 min-h-[450px] border border-gray-300 border-t-0 rounded-b-md overflow-hidden">
              <!-- Wrapper to provide style isolation -->
              <iframe
                  ref="editorFrame"
                  class="w-full h-full border-0"
                  title="Rich Text Editor"
                  sandbox="allow-same-origin"
              ></iframe>
            </div>
            
            <!-- Placeholder Editor Section -->
            <div v-if="extractedPlaceholders.length > 0" class="mt-4 p-4 border border-gray-300 rounded-md bg-gray-50">
              <h3 class="text-md font-semibold mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                Template Placeholders
              </h3>
              <p class="text-sm text-gray-600 mb-3">Edit the values for placeholders in this slide template:</p>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div v-for="(placeholder, index) in extractedPlaceholders" :key="index" class="flex flex-col">
                  <label :for="`placeholder-${index}`" class="text-xs font-medium text-gray-700 mb-1 flex items-center">
                    {{ placeholder.name }}
                    <span v-if="placeholder.applied" class="ml-2 text-xs text-green-600 font-normal">(Applied)</span>
                  </label>
                  <div class="flex">
                    <input
                      :id="`placeholder-${index}`"
                      v-model="placeholder.value"
                      type="text"
                      :class="[
                        'flex-1 px-3 py-2 border rounded-md text-sm',
                        placeholder.applied
                          ? 'bg-gray-100 border-gray-300 text-gray-700 cursor-not-allowed'
                          : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
                      ]"
                      :placeholder="placeholder.applied ? 'Value applied' : `Enter value for ${placeholder.name}`"
                      @change="updatePlaceholderInTemplate(placeholder)"
                      :readonly="placeholder.applied"
                    />
                  </div>
                </div>
              </div>
              
              <!-- Removed "Apply All Changes" button as changes are applied instantly -->
            </div>
          </div>

          <!-- Save button removed from here and moved to the top -->
        </div>

        <div v-if="!selectedSlide" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <p class="text-gray-500 mb-4">Select a slide from the left panel or create a new one</p>
            <button
                @click="addNewSlide"
                class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                :disabled="isLoading"
            >
              {{ isLoading ? 'Loading...' : 'Add New Slide Template' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Email Review Modal -->
  <EmailReviewModal
      v-if="showEmailModal"
      :isOpen="showEmailModal"
      :recipientEmail="emailRecipient"
      :process-step-type="currentProcessStepType"
      :templateVariables="templateVariables"
      @close="showEmailModal = false"
      @send-success="showEmailModal = false"
  />

  <!-- Prompt Editor Modal -->
  <div v-if="showPromptEditor" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] flex flex-col">
      <div class="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 class="text-lg font-semibold">Edit AI Prompt</h3>
        <button @click="showPromptEditor = false" class="text-gray-500 hover:text-gray-700">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="p-4 flex-1 overflow-y-auto flex flex-row gap-6">
        <!-- Instructions on the left -->
        <div class="w-1/2">
          <div>
            <p class="text-sm text-gray-600 mb-2">
              Create a custom AI prompt for this template. You can use the following placeholders:
            </p>
            <ul class="text-xs text-gray-500 list-disc pl-5 mb-4">
              <li><code>{SLIDE_TITLE}</code> - Will be replaced with the slide title</li>
              <li><code>{SLIDE_CONTENT}</code> - Will be replaced with the current slide HTML content</li>
              <li><code>{SLIDE_PLACEHOLDERS}</code> - Will be replaced with a structured list of all placeholders in the template, separated into "already applied" and "not yet applied" sections, with clear instructions for the AI on how to handle them</li>
              <li><code>{CURRENT_FORECASTING_PERIOD}</code> - Will be replaced with the current forecasting period name and date</li>
            </ul>
            
            <div class="text-xs text-gray-600 bg-gray-100 p-2 rounded-md mb-4">
              <p class="font-medium mb-1">Example of {SLIDE_PLACEHOLDERS} format:</p>
              <pre class="text-xs overflow-x-auto">
TEMPLATE PLACEHOLDERS:

ALREADY APPLIED (DO NOT CHANGE THESE):
- {YEAR} = "2025"
- {QUARTER} = "Q2"

NOT YET APPLIED (PLEASE FILL THESE):
- {REVENUE}
- {GROWTH_PERCENT}

INSTRUCTIONS FOR PLACEHOLDERS:
1. DO NOT modify already applied placeholders - keep them exactly as they are
2. Replace unapplied placeholders with appropriate values
3. Use consistent formatting for similar types of data
4. Format financial numbers with commas for thousands (e.g., 1,234)
5. Include % sign for percentages (e.g., 12.5%)
              </pre>
            </div>
          </div>
        </div>
        
        <!-- Prompt input on the right -->
        <div class="w-1/2">
          <textarea
            v-model="editingPrompt"
            class="w-full h-full min-h-[400px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Enter a custom prompt for this template..."
          ></textarea>
        </div>
      </div>

      <div class="p-4 border-t border-gray-200 flex justify-end space-x-2">
        <button
          @click="showPromptEditor = false"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
        >
          Cancel
        </button>
        <button
          @click="saveCustomPrompt"
          class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          :disabled="isSavingPrompt"
        >
          {{ isSavingPrompt ? 'Saving...' : 'Save Prompt' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {LogCategory, logger} from '@/services/logger';
import {useStorage} from '@/composables/useStorage';
import {supabase} from '@/services/supabase';
import {fissionChatbotService} from "@/services/llm";
import EmailReviewModal from '@/components/EmailReviewModal.vue';
import {DOCUMENT_STATUS} from '@/types/document';
import {systemConfigService} from '@/features/system-config';
import {useForecastingProcess} from '@/composables/useDatabase';
import {pdfExportService} from '@/services/pdfExportService';
import {ChatbotEnv} from "@/types/chatbotEnv";
import {BusinessRole} from "@/types/businessRole";

interface Slide {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  templateId?: string; // Reference to the template used to create this slide
}

interface HtmlTemplate {
  id: string;
  name: string;
  content: string;
  url?: string;
  prompt?: string; // Custom AI prompt for this template
}

export default defineComponent({
  name: 'ForecastingPCOPresentation',
  components: {
    EmailReviewModal
  },
  setup() {
    // Use the forecasting process composable to fetch processes
    const {
      processes,
      loading,
      fetchProcesses
    } = useForecastingProcess();

    // Selected process ID
    const selectedProcessId = ref<string>('');
    
    // Placeholder management
    interface Placeholder {
      name: string;
      value: string;
      original: string;
      applied: boolean; // Track if the placeholder has been applied
    }
    const extractedPlaceholders = ref<Placeholder[]>([]);

    const {downloadFile} = useStorage();
    const isExporting = ref(false);
    const isLoading = ref(false);
    const isGeneratingContent = ref(false);
    const htmlTemplates = ref<HtmlTemplate[]>([]);
    const selectedTemplateId = ref<string>('');
    const previewUrl = ref<string>('');
    const previewFrame = ref<HTMLIFrameElement | null>(null);

    // Email modal state
    const showEmailModal = ref(false);
    const emailRecipient = ref('');
    const currentProcessStepType = ref('PRESENT'); // Corresponds to DOCUMENT_STATUS.PRESENTATION
    const templateVariables = ref<Record<string, any>>({});
    
    // Presentation saving state
    const isSavingPresentation = ref(false);

    // Initialize with an empty array - slides will be created by the user
    const slides = ref<Slide[]>([]);

    // Format process date (shorter format)
    const formatProcessDate = (dateInput: string | Date): string => {
      if (!dateInput) return '';

      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    };

    // Handle process selection change
    const handleProcessChange = async () => {
      logger.info('Forecasting process selected for presentation view', LogCategory.GENERAL, {
        processId: selectedProcessId.value
      });

      if (!selectedProcessId.value) return;

      // Clear existing slides initially
      slides.value = [];
      selectedSlideIndex.value = null;
      
      isLoading.value = true;
      
      try {
        // Check if a presentation already exists for this forecasting process
        const { data: existingPresentations, error: fetchError } = await supabase
          .from('forecast_presentation')
          .select('id')
          .eq('forecasting_id', selectedProcessId.value);

        if (fetchError) {
          throw fetchError;
        }

        // If a presentation exists, load its slides
        if (existingPresentations && existingPresentations.length > 0) {
          const presentationId = existingPresentations[0].id;
          
          // Fetch slides for this presentation
          const { data: presentationSlides, error: slidesError } = await supabase
            .from('forecast_presentation_slide')
            .select('*')
            .eq('forecast_presentation_id', presentationId)
            .order('slideIndex', { ascending: true });
            
          if (slidesError) {
            throw slidesError;
          }
          
          if (presentationSlides && presentationSlides.length > 0) {
            // Convert the fetched slides to the application's slide format
            const loadedSlides: Slide[] = presentationSlides.map((dbSlide, index) => {
              // Try to extract a title from the HTML content
              let title = `Slide ${index + 1}`;
              
              // Parse the HTML to extract a title if possible
              if (dbSlide.template) {
                try {
                  // Create a temporary div to parse the HTML
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = dbSlide.template;
                  
                  // Look for heading elements that might contain a title
                  const headings = tempDiv.querySelectorAll('h1, h2, h3');
                  if (headings.length > 0) {
                    // Use the first heading as the title
                    const headingText = headings[0].textContent?.trim();
                    if (headingText && headingText.length > 0) {
                      title = headingText;
                    }
                  }
                } catch (error) {
                  console.error('Error parsing slide HTML for title:', error);
                }
              }
              
              return {
                id: dbSlide.id,
                title: title,
                content: dbSlide.template || '',
                created_at: dbSlide.created_at,
                updated_at: new Date().toISOString(),
                // We don't have templateId in the database, so we can't set it here
              };
            });
            
            // Update the slides array
            slides.value = loadedSlides;
            
            // Select the first slide if available
            if (loadedSlides.length > 0) {
              selectedSlideIndex.value = 0;
              
              // Load placeholders from database if available
              if (presentationSlides[0].placeholders) {
                try {
                  // Get the first slide's placeholders
                  const dbPlaceholders = presentationSlides[0].placeholders;
                  
                  if (Array.isArray(dbPlaceholders) && dbPlaceholders.length > 0) {
                    // Convert database placeholders to application format
                    extractedPlaceholders.value = dbPlaceholders.map(p => ({
                      name: p.name,
                      value: p.value || '',
                      original: `{${p.name}}`,
                      applied: p.applied || false
                    }));
                    
                    logger.info('Loaded placeholders from database', LogCategory.GENERAL, {
                      slideId: presentationSlides[0].id,
                      placeholderCount: extractedPlaceholders.value.length
                    });
                  } else {
                    // If no placeholders in database, extract them from the content
                    extractPlaceholdersFromSlide();
                  }
                } catch (error) {
                  console.error('Error loading placeholders from database:', error);
                  // Fall back to extracting placeholders from content
                  extractPlaceholdersFromSlide();
                }
              } else {
                // If no placeholders in database, extract them from the content
                extractPlaceholdersFromSlide();
              }
            }
            
            logger.info('Loaded existing presentation', LogCategory.GENERAL, {
              presentationId,
              slideCount: loadedSlides.length
            });
            
            // Silently load the presentation without showing an alert
          } else {
            logger.info('No slides found for existing presentation', LogCategory.GENERAL, {
              presentationId
            });
          }
        } else {
          logger.info('No existing presentation found for this forecasting process', LogCategory.GENERAL, {
            forecastingId: selectedProcessId.value
          });
        }
      } catch (error) {
        logger.error('Error loading presentation', LogCategory.GENERAL, { error });
        alert('Failed to load presentation. Please try again.');
      } finally {
        isLoading.value = false;
      }
    };

    const selectedSlideIndex = ref<number | null>(null);

    const selectedSlide = computed(() => {
      if (selectedSlideIndex.value !== null) {
        return slides.value[selectedSlideIndex.value];
      }
      return null;
    });

    // Function to create a blob URL for the preview iframe
    const createPreviewUrl = (htmlContent: string) => {
      // Create a temporary div to sanitize content if needed
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      // Create a complete HTML document with proper styling and isolation
      const fullHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body, html {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100%;
              overflow: hidden;
              font-family: Arial, sans-serif;
            }
            
            /* Ensure images don't overflow */
            img {
              max-width: 100%;
              height: auto;
            }
            
            /* Center content by default */
            body {
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: #f5f5f5;
            }
            
            /* Fixed size 16:9 container */
            .aspect-container {
              width: 640px;
              height: 360px;
              background-color: white;
              overflow: hidden;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              position: relative;
            }
            
            /* Content inside container */
            .aspect-container > * {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
            }
            
            /* Scale down text for preview - scope to container */
            .aspect-container h1 { font-size: 1.5rem !important; }
            .aspect-container h2 { font-size: 1.3rem !important; }
            .aspect-container h3 { font-size: 1.1rem !important; }
            .aspect-container h4,
            .aspect-container h5,
            .aspect-container h6 { font-size: 1rem !important; }
            .aspect-container p,
            .aspect-container div:not(.aspect-container),
            .aspect-container span { font-size: 0.9rem !important; }
            .aspect-container li { font-size: 0.9rem !important; }
          </style>
        </head>
        <body>
          <div class="aspect-container">
            ${htmlContent}
          </div>
        </body>
        </html>
      `;

      // Create a blob from the HTML content
      const blob = new Blob([fullHtml], {type: 'text/html'});

      // Create a URL for the blob
      return URL.createObjectURL(blob);
    };

    // Function to update the preview when the content changes
    const updatePreview = () => {
      if (selectedSlide.value) {
        // Revoke the old URL to prevent memory leaks
        if (previewUrl.value) {
          URL.revokeObjectURL(previewUrl.value);
        }

        // Create a new preview URL
        previewUrl.value = createPreviewUrl(selectedSlide.value.content);
      }
    };

    // Rich text editor functions
    const editorFrame = ref<HTMLIFrameElement | null>(null);
    let editorDoc: Document | null = null;
    let editorBody: HTMLElement | null = null;

    // Color picker state
    const showColorPicker = ref<false | 'text' | 'background'>(false);
    const currentTextColor = ref('#000000');
    const currentBgColor = ref('#ffffff');
    const colorOptions = [
      '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef',
      '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
      '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'
    ];

    // Toggle color picker visibility
    const toggleColorPicker = (type: 'text' | 'background') => {
      showColorPicker.value = showColorPicker.value === type ? false : type;
    };

    // Apply text color
    const applyTextColor = (color: string) => {
      if (editorDoc) {
        // Store the selected color
        currentTextColor.value = color;

        // Apply the color to the selected text
        editorDoc.execCommand('foreColor', false, color);

        // Focus back on the editor
        editorBody?.focus();

        // Update the content and preview
        if (editorBody && selectedSlide.value) {
          selectedSlide.value.content = editorBody.innerHTML;
          updatePreview();
        }

        // Hide the color picker
        showColorPicker.value = false;
      }
    };

    // Apply background color
    const applyBackgroundColor = (color: string) => {
      if (editorDoc) {
        // Store the selected color
        currentBgColor.value = color;

        // Apply the background color to the selected text
        editorDoc.execCommand('hiliteColor', false, color);

        // Focus back on the editor
        editorBody?.focus();

        // Update the content and preview
        if (editorBody && selectedSlide.value) {
          selectedSlide.value.content = editorBody.innerHTML;
          updatePreview();
        }

        // Hide the color picker
        showColorPicker.value = false;
      }
    };

    // Initialize the editor with the current content
    const initEditor = () => {
      if (!editorFrame.value || !selectedSlide.value) return;

      // Access the iframe document
      const iframe = editorFrame.value;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

      if (!iframeDoc) return;

      // Store references for later use
      editorDoc = iframeDoc;

      // Create the editor content
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body {
              margin: 0;
              padding: 10px;
              font-family: Arial, sans-serif;
              min-height: 100%;
              box-sizing: border-box;
              outline: none;
            }
            
            /* Basic styling for content */
            h1 { font-size: 2rem; margin-top: 0.5rem; margin-bottom: 0.5rem; }
            h2 { font-size: 1.5rem; margin-top: 0.5rem; margin-bottom: 0.5rem; }
            h3 { font-size: 1.25rem; margin-top: 0.5rem; margin-bottom: 0.5rem; }
            p { margin-top: 0.5rem; margin-bottom: 0.5rem; }
            
            /* Ensure all styles are contained within the iframe */
            * {
              max-width: 100%;
            }
          </style>
        </head>
        <body contenteditable="true">${selectedSlide.value.content}</body>
        </html>
      `);
      iframeDoc.close();

      // Store body reference
      editorBody = iframeDoc.body;

      // Add event listeners
      editorBody.addEventListener('input', handleContentEdit);
      editorBody.addEventListener('paste', handlePaste);

      // Set focus to the editor
      setTimeout(() => {
        editorBody?.focus();
      }, 100);
    };

    // Handle content edits
    const handleContentEdit = () => {
      if (editorBody && selectedSlide.value) {
        // Update the slide content
        selectedSlide.value.content = editorBody.innerHTML;
        updatePreview();
        
        // Re-extract placeholders when content changes
        extractPlaceholdersFromSlide();
      }
    };

    // Handle paste events to strip unwanted formatting
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain') || '';
      if (editorDoc) {
        editorDoc.execCommand('insertText', false, text);
      }
    };

    // Format text in the editor
    const formatText = (command: string) => {
      if (editorDoc) {
        editorDoc.execCommand(command, false);
        editorBody?.focus();
        if (editorBody && selectedSlide.value) {
          selectedSlide.value.content = editorBody.innerHTML;
          updatePreview();
        }
      }
    };

    // Watch for changes in the selected slide
    watch(selectedSlideIndex, () => {
      updatePreview();
      // Initialize the editor with the new content
      setTimeout(initEditor, 100);

      // Close the prompt editor if it's open to ensure a fresh state when reopened
      if (showPromptEditor.value) {
        showPromptEditor.value = false;
      }
      
      // Extract placeholders from the new slide
      extractPlaceholdersFromSlide();
    });
    
    // Function to extract placeholders from the current slide content
    const extractPlaceholdersFromSlide = () => {
      if (!selectedSlide.value) {
        extractedPlaceholders.value = [];
        return;
      }
      
      const content = selectedSlide.value.content;
      // Modified regex to match ALL CAPS placeholders with dots
      const placeholderRegex = /\{([A-Z0-9_.]+)\}/g;
      const matches = [...content.matchAll(placeholderRegex)];
      
      // Create a map to deduplicate placeholders
      const placeholderMap = new Map<string, Placeholder>();
      
      matches.forEach(match => {
        const fullMatch = match[0]; // e.g., "{PLACEHOLDER}"
        const name = match[1].trim(); // e.g., "PLACEHOLDER"
        
        if (!placeholderMap.has(name)) {
          placeholderMap.set(name, {
            name,
            value: '', // Initially empty, will be filled by user
            original: fullMatch,
            applied: false // Initially not applied
          });
        }
      });
      
      // Convert map to array
      extractedPlaceholders.value = Array.from(placeholderMap.values());
      
      logger.info('Extracted placeholders from slide', LogCategory.GENERAL, {
        slideId: selectedSlide.value.id,
        placeholderCount: extractedPlaceholders.value.length
      });
    };
    
    // Function to update a single placeholder in the template
    const updatePlaceholderInTemplate = (placeholder: Placeholder) => {
      if (!selectedSlide.value || !editorBody) return;
      
      // Only update if the placeholder has a value
      if (!placeholder.value) {
        return; // Skip update if no value is set
      }
      
      // Update the HTML content by replacing the placeholder with its value
      const updatedContent = selectedSlide.value.content.replace(
        new RegExp(`\\{${placeholder.name}\\}`, 'g'),
        placeholder.value
      );
      
      // Update the slide content
      selectedSlide.value.content = updatedContent;
      
      // Update the editor content
      editorBody.innerHTML = updatedContent;
      
      // Update the preview
      updatePreview();
      
      // Mark this placeholder as applied
      placeholder.applied = true;
      
      logger.info('Updated placeholder in template', LogCategory.GENERAL, {
        placeholder: placeholder.name,
        value: placeholder.value,
        applied: true
      });
    };
    
    // Function to apply all placeholder changes at once
    const applyAllPlaceholders = () => {
      if (!selectedSlide.value || !editorBody) return;
      
      let updatedContent = selectedSlide.value.content;
      
      // Apply only placeholder values that have been set
      extractedPlaceholders.value.forEach(placeholder => {
        // Only replace placeholders that have a value set
        if (placeholder.value) {
          updatedContent = updatedContent.replace(
            new RegExp(`\\{${placeholder.name}\\}`, 'g'),
            placeholder.value
          );
          
          // Mark this placeholder as applied
          placeholder.applied = true;
        }
      });
      
      // Update the slide content
      selectedSlide.value.content = updatedContent;
      
      // Update the editor content
      editorBody.innerHTML = updatedContent;
      
      // Update the preview
      updatePreview();
      
      logger.info('Applied all placeholder changes', LogCategory.GENERAL, {
        slideId: selectedSlide.value.id,
        placeholderCount: extractedPlaceholders.value.length
      });
    };

    // Function to fetch HTML templates from the files table
    const fetchHtmlTemplates = async () => {
      isLoading.value = true;
      try {
        // Query the files table for files with type = "html"
        const {data: filesData, error: filesError} = await supabase
            .from('files')
            .select('*')
            .eq('type', 'html');

        if (filesError) {
          throw filesError;
        }
        
        // Fetch the custom prompts for these templates
        const fileIds = filesData.map(file => file.id);
        const {data: promptsData, error: promptsError} = await supabase
            .from('slide_template_prompts')
            .select('*')
            .in('file_id', fileIds);

        if (promptsError) {
          logger.warn('Error fetching template prompts', LogCategory.GENERAL, {
            error: promptsError
          });
          // Continue without prompts if there's an error
        }

        // Create a map of file_id to prompt for easy lookup
        const promptsMap = new Map();
        if (promptsData && promptsData.length > 0) {
          promptsData.forEach(prompt => {
            promptsMap.set(prompt.file_id, prompt.prompt_text);
          });
        }

        if (!filesData || filesData.length === 0) {
          logger.info('No HTML templates found in the files table', LogCategory.GENERAL);
          htmlTemplates.value = [];
          return;
        }

        // Create template objects from the files data
        const templates: HtmlTemplate[] = [];

        for (const file of filesData) {
          try {
            // Extract the bucket and path from the URL
            const url = file.url;
            const urlParts = url.split('/');
            const bucketIndex = urlParts.findIndex(part => part === 'object') + 1;

            if (bucketIndex > 0 && bucketIndex < urlParts.length) {
              const bucket = urlParts[bucketIndex];
              const path = urlParts.slice(bucketIndex + 1).join('/');

              // Download the file content
              const fileData = await downloadFile(bucket, path);

              // Convert the file data to text
              const content = await fileData.text();

              // Create a template object with prompt if available
              templates.push({
                id: file.id,
                name: file.name.replace('.html', ''),
                content,
                url: file.url,
                prompt: promptsMap.get(file.id) // Add the custom prompt if available
              });

              logger.info('HTML template loaded', LogCategory.GENERAL, {
                id: file.id,
                name: file.name
              });
            } else {
              logger.warn('Invalid URL format for HTML template', LogCategory.GENERAL, {
                url,
                fileId: file.id
              });
            }
          } catch (err) {
            logger.error('Error downloading HTML template', LogCategory.GENERAL, {
              error: err,
              fileId: file.id,
              fileName: file.name
            });
          }
        }

        // Update the templates ref
        htmlTemplates.value = templates;

        // Set the default selected template if available
        if (templates.length > 0) {
          selectedTemplateId.value = templates[0].id;
        }

        logger.info('HTML templates loaded from files table', LogCategory.GENERAL, {
          count: templates.length
        });
      } catch (err) {
        logger.error('Error fetching HTML templates from files table', LogCategory.GENERAL, {
          error: err
        });
      } finally {
        isLoading.value = false;
      }
    };

    // Function to get template content by ID
    const getTemplateContent = (templateId: string): string => {
      const template = htmlTemplates.value.find(t => t.id === templateId);
      return template ? template.content : '';
    };

    // Function to select a slide
    const selectSlide = (index: number) => {
      selectedSlideIndex.value = index;
    };

    // Function to add a new slide
    const addNewSlide = async () => {
      isLoading.value = true;

      try {
        let content = '';
        let title = 'New Slide';

        // If a template is selected, use its content and name
        if (selectedTemplateId.value) {
          const selectedTemplate = htmlTemplates.value.find(t => t.id === selectedTemplateId.value);
          content = getTemplateContent(selectedTemplateId.value);

          // Use the template name as the slide title if available
          if (selectedTemplate) {
            title = selectedTemplate.name;
          }
        }

        const newSlide: Slide = {
          id: Date.now().toString(),
          title,
          content,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          templateId: selectedTemplateId.value // Store the template ID with the slide
        };

        slides.value.push(newSlide);
        selectedSlideIndex.value = slides.value.length - 1;

        logger.info('New slide created', LogCategory.GENERAL, {
          slideId: newSlide.id,
          templateId: selectedTemplateId.value
        });
      } catch (err) {
        logger.error('Error creating new slide', LogCategory.GENERAL, {
          error: err
        });
      } finally {
        isLoading.value = false;
      }
    };

    // Function to save slide changes
    const saveSlide = () => {
      if (selectedSlideIndex.value !== null && selectedSlide.value) {
        // In a real app, this would save to a database
        selectedSlide.value.updated_at = new Date().toISOString();

        // Update the preview
        updatePreview();

        logger.info('Slide updated', LogCategory.GENERAL, {
          slideId: selectedSlide.value.id,
          title: selectedSlide.value.title
        });
      }
    };

    // Function to fill slide content with AI-generated content
    const fillContentWithAI = async () => {
      if (!selectedSlide.value) return;

      isGeneratingContent.value = true;

      try {
        // Extract placeholders from the content with single curly braces
        const content = selectedSlide.value.content;
        const placeholderRegex = /\{([^}]+)\}/g;
        const placeholders = [...content.matchAll(placeholderRegex)].map(match => match[1].trim());

        // Find the template that was used for this slide
        let customPrompt: string | null = null;

        console.log('Selected slide:', selectedSlide.value);
        console.log('Selected slide templateId:', selectedSlide.value?.templateId);

        // First try to get the template from the slide's templateId
        if (selectedSlide.value && selectedSlide.value.templateId) {
          const templateId = selectedSlide.value.templateId;
          console.log('Looking for template with ID:', templateId);

          // Fetch the latest prompt directly from the database
          try {
            const { data: promptData, error: promptError } = await supabase
              .from('slide_template_prompts')
              .select('prompt_text')
              .eq('file_id', templateId)
              .single();

            if (promptError && promptError.code !== 'PGRST116') {
              console.error('Error fetching prompt:', promptError);
            } else if (promptData) {
              console.log('Found prompt in database:', promptData.prompt_text);
              customPrompt = promptData.prompt_text;

              // Also update the template in memory
              const templateIndex = htmlTemplates.value.findIndex(t => t.id === templateId);
              if (templateIndex >= 0) {
                htmlTemplates.value[templateIndex].prompt = promptData.prompt_text;
              }
            } else {
              console.log('No prompt found in database for template ID:', templateId);

              // Fall back to the in-memory template
              const template = htmlTemplates.value.find(t => t.id === templateId);
              if (template && template.prompt) {
                console.log('Using in-memory prompt:', template.prompt);
                customPrompt = template.prompt;
              }
            }
          } catch (error) {
            console.error('Error fetching prompt from database:', error);
          }
        }
        // If no template ID on the slide or no prompt found, try the currently selected template
        else if (selectedTemplateId.value) {
          console.log('No templateId on slide, using selected template ID:', selectedTemplateId.value);

          const template = htmlTemplates.value.find(t => t.id === selectedTemplateId.value);
          if (template && template.prompt) {
            console.log('Using prompt from selected template:', template.prompt);
            customPrompt = template.prompt;
          }
        }

        // Create a prompt that requests HTML output with appropriate instructions
        let promptText: string;

        if (customPrompt) {
          // Use the custom template-specific prompt if available
          console.log('Using custom prompt:', customPrompt);
          logger.info('Using custom prompt for template', LogCategory.GENERAL, {
            templateId: selectedSlide.value?.templateId || selectedTemplateId.value
          });

          // Replace any placeholders in the custom prompt
          let processedPrompt = customPrompt.replace('{SLIDE_TITLE}', selectedSlide.value.title);

          // Check if the custom prompt includes the {SLIDE_CONTENT} placeholder
          if (processedPrompt.includes('{SLIDE_CONTENT}')) {
            // Replace the placeholder with the actual content
            processedPrompt = processedPrompt.replace('{SLIDE_CONTENT}', selectedSlide.value.content);
          } else {
            // If the custom prompt doesn't include the {SLIDE_CONTENT} placeholder, append the HTML content
            processedPrompt += `\n\nHere is the current HTML content of the slide:\n${selectedSlide.value.content}\n\nPlease use this HTML content as a reference and return a complete, valid HTML that I can use directly in my presentation.`;
          }
          
          // Check if the custom prompt includes the {SLIDE_PLACEHOLDERS} placeholder
          if (processedPrompt.includes('{SLIDE_PLACEHOLDERS}')) {
            // Get applied and unapplied placeholders
            const appliedPlaceholders = extractedPlaceholders.value.filter(p => p.applied);
            const unappliedPlaceholders = extractedPlaceholders.value.filter(p => !p.applied);
            
            // Format the placeholders in a structured way with instructions
            let placeholdersString = "TEMPLATE PLACEHOLDERS:\n";
            
            // Add applied placeholders section
            if (appliedPlaceholders.length > 0) {
              placeholdersString += "\nALREADY APPLIED (DO NOT CHANGE THESE):\n";
              appliedPlaceholders.forEach(p => {
                placeholdersString += `- {${p.name}} = "${p.value}"\n`;
              });
            }
            
            // Add unapplied placeholders section
            if (unappliedPlaceholders.length > 0) {
              placeholdersString += "\nNOT YET APPLIED (PLEASE FILL THESE):\n";
              unappliedPlaceholders.forEach(p => {
                placeholdersString += `- {${p.name}}\n`;
              });
            }
            
            // Add instructions for handling placeholders
            placeholdersString += "\nINSTRUCTIONS FOR PLACEHOLDERS:\n";
            placeholdersString += "1. DO NOT modify already applied placeholders - keep them exactly as they are\n";
            placeholdersString += "2. Replace unapplied placeholders with appropriate values\n";
            
            // Replace the placeholder with the formatted placeholders
            processedPrompt = processedPrompt.replace(
              '{SLIDE_PLACEHOLDERS}',
              extractedPlaceholders.value.length > 0 ? placeholdersString : 'No placeholders found in the template'
            );
            
            console.log('Added placeholders to prompt:', placeholdersString);
          }

          // Check if the custom prompt includes the {CURRENT_FORECASTING_PERIOD} placeholder
          if (processedPrompt.includes('{CURRENT_FORECASTING_PERIOD}')) {
            // Get the selected process name and date
            let forecastingPeriodText = 'current forecasting period';
            
            if (selectedProcessId.value) {
              const selectedProcess = processes.value.find(p => p.id === selectedProcessId.value);
              if (selectedProcess) {
                forecastingPeriodText = `${selectedProcess.name} (${formatProcessDate(selectedProcess.start_date)})`;
              }
            }
            
            // Replace the placeholder with the actual forecasting period
            processedPrompt = processedPrompt.replace('{CURRENT_FORECASTING_PERIOD}', forecastingPeriodText);
            console.log('Added forecasting period:', forecastingPeriodText);
          }

          promptText = processedPrompt;
          console.log('Final prompt with replacements:', promptText);
        } else if (placeholders.length > 0) {
          // Default prompt for slides with placeholders
          promptText = `I have a Fraport financial presentation slide with HTML content that contains placeholders in single curly braces like {PLACEHOLDER_NAME}.

IMPORTANT: I need you to return the COMPLETE HTML with the placeholders filled in with realistic mock financial data. Do not modify the HTML structure or styling.

Here is the current HTML content:
${selectedSlide.value.content}

Instructions:
1. Replace all placeholders (text in single curly braces like {YEAR}, {PAX_ACTUAL}, etc.) with appropriate realistic financial data.
2. For financial figures, use proper number formatting (e.g., "1,445" for thousands).
3. For percentages, include the % sign (e.g., "8%" not just "8").
4. Ensure negative values are properly formatted (they will automatically appear red due to the CSS).
5. Keep all decimal values consistent (typically 0 decimal places for whole numbers).
6. Use coherent data that makes mathematical sense (e.g., subtotals should add up correctly).
7. Remember that Airport Services, Ground Handling, and Non-Aeronautical Revenues should sum to Operating Revenues.
8. Ensure year-over-year and quarter-over-quarter changes are realistic.
9. Do not modify any HTML tags, classes, or styling - only replace placeholder content.

Your response should contain ONLY valid HTML that I can directly use in my presentation slide. Do not include any explanation or markdown formatting.`;
        } else {
          // Default prompt for slides without placeholders
          promptText = `I have a Fraport financial presentation slide with the title: "${selectedSlide.value.title}".

Please generate complete HTML content for this financial presentation slide, including realistic financial data of the current quarter, metrics, and formatting appropriate for an airport management company's quarterly financial reporting.

Your response should contain ONLY valid HTML that I can directly use in my presentation slide.`;
        }

        // Get or create a presentation ID for the current forecasting process to use as conversation ID
        let conversationId;
        let slideDbId = ''; // Database ID of the current slide
        
        try {
          // Check if a presentation already exists for this forecasting process
          const { data: existingPresentations, error: fetchError } = await supabase
            .from('forecast_presentation')
            .select('id')
            .eq('forecasting_id', selectedProcessId.value);
            
          if (!fetchError && existingPresentations && existingPresentations.length > 0) {
            // Use the existing presentation ID as the conversation ID
            conversationId = existingPresentations[0].id;
            console.log('Using existing presentation ID as conversation ID:', conversationId);
            
            // If we have a selected slide, check if it exists in the database
            if (selectedSlideIndex.value !== null) {
              // Check if this slide already exists in the database
              const { data: existingSlides, error: slidesError } = await supabase
                .from('forecast_presentation_slide')
                .select('id')
                .eq('forecast_presentation_id', conversationId)
                .eq('slideIndex', selectedSlideIndex.value);
                
              if (!slidesError && existingSlides && existingSlides.length > 0) {
                // Use the existing slide ID as the reference ID
                slideDbId = existingSlides[0].id;
                console.log('Using existing slide ID as reference ID:', slideDbId);
              }
            }
          } else {
            // No presentation exists, create one first
            console.log('No presentation found, creating a new one...');
            
            // Create a new presentation
            const { data: newPresentation, error: insertError } = await supabase
              .from('forecast_presentation')
              .insert({
                forecasting_id: selectedProcessId.value
              })
              .select('id')
              .single();
              
            if (insertError || !newPresentation) {
              throw insertError || new Error('Failed to create presentation');
            }
            
            // Use the newly created presentation ID
            conversationId = newPresentation.id;
            console.log('Created new presentation with ID:', conversationId);
            
            // Save the current slides to this presentation
            if (slides.value.length > 0) {
              const slidesToInsert = slides.value.map((slide, index) => ({
                forecast_presentation_id: conversationId,
                template: slide.content,
                slideIndex: index,
                isFulfilled: false
              }));
              
              const { data: insertedSlides, error: slidesInsertError } = await supabase
                .from('forecast_presentation_slide')
                .insert(slidesToInsert)
                .select('id, slideIndex');
                
              if (slidesInsertError) {
                console.error('Error saving slides to new presentation:', slidesInsertError);
                // Continue anyway, as we at least have the presentation ID
              } else {
                console.log('Saved slides to new presentation');
                
                // Find the current slide in the inserted slides
                if (insertedSlides && selectedSlideIndex.value !== null) {
                  const currentSlide = insertedSlides.find(s => s.slideIndex === selectedSlideIndex.value);
                  if (currentSlide) {
                    slideDbId = currentSlide.id;
                    console.log('Using newly created slide ID as reference ID:', slideDbId);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error getting/creating presentation ID:', error);
          // Create a fallback ID if all else fails
          conversationId = 'presentation-' + Date.now().toString();
          console.log('Using fallback presentation ID:', conversationId);
        }
        
        // Call the OpenAI API with a system prompt that requests HTML output
        const response = await fissionChatbotService.sendRequest({
          userMessage: promptText,
          environment: ChatbotEnv.GLOBAL,
          businessRole: BusinessRole.HEAD_OF_PCO,
          userId: selectedProcessId.value, // Use the forecasting process ID as the user ID
          conversationId: conversationId, // Use the presentation ID as the conversation ID
          isFastmodeActive: false,
          isPresentationMode: true,
          referenceId: slideDbId // Use the slide database ID as the reference ID
        });

        // Process the response - use the HTML directly
        if (editorBody && selectedSlide.value) {
          // Extract HTML content from the response - this should be clean HTML already
          let htmlContent = response.content;

          // Clean up the response if needed (remove markdown code blocks if present)
          if (htmlContent.includes('```html')) {
            htmlContent = htmlContent.replace(/```html\n/g, '').replace(/```/g, '');
          }

          // Update the slide content
          selectedSlide.value.content = htmlContent;
          editorBody.innerHTML = htmlContent;
          updatePreview();
          saveSlide();
        }

        logger.info('Financial content generated for slide', LogCategory.GENERAL, {
          slideId: selectedSlide.value.id,
          title: selectedSlide.value.title
        });
      } catch (error) {
        logger.error('Error generating financial content', LogCategory.GENERAL, {
          error,
          slideId: selectedSlide.value?.id
        });
        alert('Failed to generate content. Please try again.');
      } finally {
        isGeneratingContent.value = false;
      }
    };

    // Function to open a preview in a new tab
    const openPreviewInNewTab = (index: number) => {
      const slide = slides.value[index];
      if (!slide) return;

      // Use the slide content directly without any additional processing
      const processedContent = slide.content;

      // Create HTML content with print-friendly styles
      const htmlContent = createPrintableHtml(slide.title, processedContent);

      // Create a blob from the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });

      // Create a URL for the blob
      const blobUrl = URL.createObjectURL(blob);

      // Open the URL in a new tab
      window.open(blobUrl, '_blank');

      // Clean up the blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 5000);

      logger.info('Slide opened in new tab', LogCategory.GENERAL, {
        slideId: slide.id,
        title: slide.title
      });
    };

            // Function to create printable HTML content - only the pure slide content
            const createPrintableHtml = (title: string, content: string) => {
              // For pure content printing, we'll use a minimal HTML structure with no wrappers
              return `
          <!DOCTYPE html>
          <html>
          <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${title}</title>
          <style>
          /* Absolutely minimal styling for print - only what's necessary for backgrounds */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          body, html {
            margin: 0;
            padding: 0;
            background-color: white;
          }

          @media print {
            @page {
              size: landscape;
              margin: 0;
            }
          }
          </style>
          </head>
          <body>
          ${content}
          </body>
          </html>
          `;
            };

            // Function to export all slides to PDF using the PDF Export Service
        const exportAllSlides = async () => {
          isExporting.value = true;

          try {
            // Validate that we have slides to export
            if (slides.value.length === 0) {
              alert('No slides to export. Please create at least one slide first.');
              isExporting.value = false;
              return;
            }

            // Create a copy of slides to work with
            const slidesToExport = [...slides.value];

            // Log the slides being exported
            console.log(`Exporting ${slidesToExport.length} slides to PDF`);

            // Get process name for the filename
            const processName = processes.value.find(p => p.id === selectedProcessId.value)?.name || 'Presentation';
            const sanitizedProcessName = processName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
            const filename = `${sanitizedProcessName}_slides.pdf`;

            logger.info('Starting PDF export for all slides', LogCategory.GENERAL, {
              slideCount: slidesToExport.length,
              filename
            });

            // Process slides without adding page numbers or borders
            const processedSlides = slidesToExport.map((slide) => {
              // Use the slide content directly without adding extra elements
              return {
                ...slide,
                content: slide.content
              };
            });

            // Export the slides to PDF with minimal settings - no margins, no logging
            await pdfExportService.exportSlidesToPdf(
              processedSlides,
              filename,
              {
                margin: 0,
                html2canvas: {
                  scale: 2,
                  useCORS: true,
                  letterRendering: true,
                  logging: false
                },
                jsPDF: {
                  unit: 'mm',
                  format: 'a4',
                  orientation: 'landscape',
                  compress: true
                }
              }
            );

            logger.info('PDF export completed successfully', LogCategory.GENERAL, {
              slideCount: slidesToExport.length,
              filename
            });
          } catch (error) {
            logger.error('PDF export failed', LogCategory.GENERAL, {
              error
            });
            alert('Failed to export slides to PDF. Please try again.');
          } finally {
            isExporting.value = false;
          }
        };


            // Function to delete a slide
            const deleteSlide = (index: number) => {
              if (index < 0 || index >= slides.value.length) return;

              const slideId = slides.value[index].id;
              slides.value.splice(index, 1);

              // Update selected slide index
              if (slides.value.length === 0) {
                selectedSlideIndex.value = null;
              } else if (selectedSlideIndex.value !== null && selectedSlideIndex.value >= index) {
                // If we deleted the selected slide or one before it, adjust the index
                selectedSlideIndex.value = Math.max(0, selectedSlideIndex.value - 1);
              }

              logger.info('Slide deleted', LogCategory.GENERAL, {
                slideId
              });
            };

            // Function to move a slide up or down in the order
            const moveSlide = (index: number, direction: 'up' | 'down') => {
              if (index < 0 || index >= slides.value.length) return;

              // Calculate the new index
              const newIndex = direction === 'up' ? index - 1 : index + 1;

              // Check if the new index is valid
              if (newIndex < 0 || newIndex >= slides.value.length) return;

              // Get the slide to move
              const slideToMove = slides.value[index];

              // Remove the slide from its current position
              slides.value.splice(index, 1);

              // Insert the slide at the new position
              slides.value.splice(newIndex, 0, slideToMove);

              // Update the selected slide index if needed
              if (selectedSlideIndex.value === index) {
                selectedSlideIndex.value = newIndex;
              } else if (selectedSlideIndex.value === newIndex) {
                selectedSlideIndex.value = index;
              }

              logger.info('Slide reordered', LogCategory.GENERAL, {
                slideId: slideToMove.id,
                direction,
                fromIndex: index,
                toIndex: newIndex
              });
            };

    // Function to save the presentation to the database
    const savePresentation = async () => {
      if (!selectedProcessId.value) {
        alert('Please select a forecasting process first');
        return;
      }

      if (slides.value.length === 0) {
        alert('Please add at least one slide before saving');
        return;
      }

      isSavingPresentation.value = true;

      try {
        // Check if a presentation already exists for this forecasting process
        const { data: existingPresentations, error: fetchError } = await supabase
          .from('forecast_presentation')
          .select('id')
          .eq('forecasting_id', selectedProcessId.value);

        if (fetchError) {
          throw fetchError;
        }

        let presentationId: string;

        // If a presentation exists, use its ID; otherwise, create a new one
        if (existingPresentations && existingPresentations.length > 0) {
          presentationId = existingPresentations[0].id;
          
          // Delete existing slides for this presentation
          const { error: deleteError } = await supabase
            .from('forecast_presentation_slide')
            .delete()
            .eq('forecast_presentation_id', presentationId);
            
          if (deleteError) {
            throw deleteError;
          }
          
          logger.info('Deleted existing slides for presentation update', LogCategory.GENERAL, {
            presentationId,
            slideCount: slides.value.length
          });
        } else {
          // Create a new presentation
          const { data: newPresentation, error: insertError } = await supabase
            .from('forecast_presentation')
            .insert({
              forecasting_id: selectedProcessId.value
            })
            .select('id')
            .single();

          if (insertError || !newPresentation) {
            throw insertError || new Error('Failed to create presentation');
          }

          presentationId = newPresentation.id;
          
          logger.info('Created new presentation', LogCategory.GENERAL, {
            presentationId,
            forecastingId: selectedProcessId.value
          });
        }

        // Insert all slides
        const slidesToInsert = slides.value.map((slide, index) => {
          // Get the placeholders for this slide
          const slideIndex = index;
          const currentSlideIndex = selectedSlideIndex.value;
          
          // If this is the currently selected slide, use the extractedPlaceholders
          // Otherwise, we need to extract placeholders from the slide content
          let slidePlaceholders: Placeholder[] = [];
          
          if (slideIndex === currentSlideIndex) {
            // Use the current extractedPlaceholders
            slidePlaceholders = extractedPlaceholders.value;
          } else {
            // Extract placeholders from the slide content
            const content = slide.content;
            const placeholderRegex = /\{([A-Z0-9_.]+)\}/g;
            const matches = [...content.matchAll(placeholderRegex)];
            
            // Create placeholder objects
            const placeholderMap = new Map<string, Placeholder>();
            
            matches.forEach(match => {
              const fullMatch = match[0]; // e.g., "{PLACEHOLDER}"
              const name = match[1].trim(); // e.g., "PLACEHOLDER"
              
              if (!placeholderMap.has(name)) {
                placeholderMap.set(name, {
                  name,
                  value: '', // We don't know the value for non-selected slides
                  original: fullMatch,
                  applied: false // We don't know if it's applied for non-selected slides
                });
              }
            });
            
            // Convert map to array
            slidePlaceholders = Array.from(placeholderMap.values());
          }
          
          // Convert placeholders to JSONB format
          const placeholdersJson = slidePlaceholders.map(p => ({
            name: p.name,
            value: p.value,
            applied: p.applied
          }));
          
          return {
            forecast_presentation_id: presentationId,
            template: slide.content,
            slideIndex: index,
            isFulfilled: false, // Assuming all slides are fulfilled since they have content
            placeholders: placeholdersJson.length > 0 ? placeholdersJson : null
          };
        });

        const { error: slidesInsertError } = await supabase
          .from('forecast_presentation_slide')
          .insert(slidesToInsert);

        if (slidesInsertError) {
          throw slidesInsertError;
        }

        logger.info('Saved presentation successfully', LogCategory.GENERAL, {
          presentationId,
          slideCount: slides.value.length
        });

        alert('Presentation saved successfully!');
      } catch (error) {
        logger.error('Error saving presentation', LogCategory.GENERAL, { error });
        alert('Failed to save presentation. Please try again.');
      } finally {
        isSavingPresentation.value = false;
      }
    };

    // Function to open the email modal
    // TODO: You have to connect this to the email-service later on !!!
    const openEmailModal = async () => {
      try {
        // Fetch board meeting attendees from system configuration
        const configData = await systemConfigService.getAllConfig();
        const boardMeetingAttendeesConfig = configData.find(item => item.property === 'board_meeting_attendees');
        const attendees = boardMeetingAttendeesConfig?.value || [];

        // Set the recipient email to the list of board meeting attendees
        if (attendees.length > 0) {
          emailRecipient.value = attendees.join(', ');
          logger.info('Using board meeting attendees as email recipients', LogCategory.GENERAL, {
            attendeesCount: attendees.length
          });
        } else {
          // Fallback to empty if no attendees are configured
          emailRecipient.value = '';
          logger.warn('No board meeting attendees configured', LogCategory.GENERAL);
        }

        // Populate template variables
        templateVariables.value = {
          documentName: 'Presentation Slides',
          documentDescription: 'Financial presentation slides for Fraport',
          documentStatus: DOCUMENT_STATUS.PRESENTATION,
          processName: 'Presentation Process',
          forecastNumber: 'F' + slides.value.length.toString().padStart(2, '0'),
          slideCount: slides.value.length
        };

        // Show the modal
        showEmailModal.value = true;

        // We need to wait for the modal to be mounted before we can access its methods
        // This will be handled by the EmailReviewModal component's onMounted hook

        logger.info('Email modal opened for presentation', LogCategory.GENERAL, {
          processStepType: currentProcessStepType.value,
          documentStatus: DOCUMENT_STATUS.PRESENTATION,
          recipientsCount: attendees.length
        });
      } catch (error) {
        logger.error('Error fetching board meeting attendees', LogCategory.GENERAL, { error });
        // Fallback to empty recipient if there's an error
        emailRecipient.value = '';

        // Still show the modal even if there's an error
        showEmailModal.value = true;
      }
    };


            // Status-related functionality removed as requested

            // Fetch processes and HTML templates when the component is mounted
            onMounted(async () => {
              // First fetch all processes
              await fetchProcesses();

              // Then fetch HTML templates
              await fetchHtmlTemplates();
              
              // Select the newest process (they're already sorted by created_at in descending order)
              if (processes.value.length > 0) {
                selectedProcessId.value = processes.value[0].id;
                
                // Call handleProcessChange to load any existing presentation for the default process
                await handleProcessChange();
                
                // Initialize the editor after the presentation is loaded
                setTimeout(initEditor, 100);
              } else {
                // If no processes are available, just initialize the editor
                setTimeout(initEditor, 100);
              }
            });

            // Prompt editor state and methods
            const showPromptEditor = ref(false);
            const editingPrompt = ref('');
            const selectedTemplateName = ref('');
            const isSavingPrompt = ref(false);

            // Function to open the prompt editor
            const openPromptEditor = async () => {
              if (!selectedSlide.value) {
                alert('Please select a slide first');
                return;
              }

              // Get the template ID from the slide or use the currently selected template
              let templateId = selectedSlide.value.templateId;

              // If no template ID is stored with the slide, use the currently selected template
              if (!templateId) {
                if (!selectedTemplateId.value) {
                  alert('No template associated with this slide');
                  return;
                }
                templateId = selectedTemplateId.value;
              }

              console.log('Looking for template with ID:', templateId);

              // Fetch the latest template data directly from the database to ensure we have the most up-to-date prompt
              try {
                // First get the template from our local cache
                const template = htmlTemplates.value.find(t => t.id === templateId);
                if (!template) {
                  alert('Template not found');
                  return;
                }

                // Then fetch the latest prompt from the database
                const { data: promptData, error: promptError } = await supabase
                  .from('slide_template_prompts')
                  .select('prompt_text')
                  .eq('file_id', templateId)
                  .single();

                selectedTemplateName.value = template.name;

                if (promptError && promptError.code !== 'PGRST116') {
                  console.error('Error fetching prompt:', promptError);
                  editingPrompt.value = template.prompt || '';
                } else if (promptData) {
                  console.log('Found prompt in database:', promptData.prompt_text);
                  editingPrompt.value = promptData.prompt_text;
                  // Update the template in memory too
                  template.prompt = promptData.prompt_text;
                } else {
                  console.log('No prompt found in database, using default');
                  editingPrompt.value = template.prompt || '';
                }

                showPromptEditor.value = true;

                // Log for debugging
                console.log('Opening prompt editor for template:', {
                  templateId,
                  templateName: template.name,
                  prompt: editingPrompt.value
                });
              } catch (error) {
                console.error('Error in openPromptEditor:', error);
                alert('Error loading prompt. Please try again.');
              }
            };

            // Function to save the custom prompt
            const saveCustomPrompt = async () => {
              if (!selectedSlide.value) return;

              // Get the template ID from the slide or use the currently selected template
              let templateId = selectedSlide.value.templateId;

              // If no template ID is stored with the slide, use the currently selected template
              if (!templateId) {
                if (!selectedTemplateId.value) {
                  alert('No template associated with this slide');
                  return;
                }
                templateId = selectedTemplateId.value;
              }

              isSavingPrompt.value = true;

              try {
                // Check if a prompt already exists for this template
                const {data: existingPrompt, error: checkError} = await supabase
                  .from('slide_template_prompts')
                  .select('*')
                  .eq('file_id', templateId)
                  .single();

                if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found" error
                  throw checkError;
                }

                let result;

                if (existingPrompt) {
                  // Update existing prompt
                  result = await supabase
                    .from('slide_template_prompts')
                    .update({
                      prompt_text: editingPrompt.value,
                      updated_at: new Date().toISOString()
                    })
                    .eq('file_id', templateId);
                } else {
                  // Insert new prompt
                  result = await supabase
                    .from('slide_template_prompts')
                    .insert({
                      file_id: templateId,
                      prompt_text: editingPrompt.value
                    });
                }

                if (result.error) {
                  throw result.error;
                }

                // Update the template in memory
                const templateIndex = htmlTemplates.value.findIndex(t => t.id === templateId);
                if (templateIndex >= 0) {
                  htmlTemplates.value[templateIndex].prompt = editingPrompt.value;
                }

                // If this slide doesn't have a templateId yet, assign it
                if (!selectedSlide.value.templateId) {
                  selectedSlide.value.templateId = templateId;
                }

                logger.info('Custom prompt saved for template', LogCategory.GENERAL, {
                  templateId,
                  templateName: selectedTemplateName.value
                });

                // Close the editor
                showPromptEditor.value = false;
              } catch (error) {
                logger.error('Error saving custom prompt', LogCategory.GENERAL, {
                  error,
                  templateId: selectedTemplateId.value
                });
                alert('Failed to save custom prompt. Please try again.');
              } finally {
                isSavingPrompt.value = false;
              }
            };

            return {
              // Process data
              processes,
              loading,
              selectedProcessId,
              handleProcessChange,
              formatProcessDate,

              // Slide data
              slides,
              selectedSlideIndex,
              selectedSlide,
              htmlTemplates,
              selectedTemplateId,
              isLoading,
              isGeneratingContent,
              previewUrl,
              previewFrame,
              editorFrame,
              showColorPicker,
              currentTextColor,
              currentBgColor,
              colorOptions,
              
              // Placeholder management
              extractedPlaceholders,
              updatePlaceholderInTemplate,
              applyAllPlaceholders,
              extractPlaceholdersFromSlide,
              
              // Email modal
              showEmailModal,
              emailRecipient,
              currentProcessStepType,
              templateVariables,
              openEmailModal,
              DOCUMENT_STATUS,
              selectSlide,
              addNewSlide,
              saveSlide,
              openPreviewInNewTab,
              fillContentWithAI,
              deleteSlide,
              moveSlide,
              updatePreview,
              formatText,
              toggleColorPicker,
              applyTextColor,
              applyBackgroundColor,
              handleContentEdit,
              handlePaste,
              isExporting,
              exportAllSlides,

              // Prompt editor
              showPromptEditor,
              editingPrompt,
              selectedTemplateName,
              isSavingPrompt,
              openPromptEditor,
              saveCustomPrompt,
              
              // Presentation saving
              isSavingPresentation,
              savePresentation,
            };
          }
        });
</script>
